import React from 'react';
import { Grid, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { Visibility, VisibilityOff, Autorenew as AutorenewIcon } from '@mui/icons-material';
import { generateStrongPassword } from '@utils/passwordValidator';
import type { SignupFormValues } from '../../hooks/useSignupForm';

interface AdminAccountFieldsProps {
  values: SignupFormValues;
  fieldErrors: Partial<Record<keyof SignupFormValues, string>>;
  handleChange: (field: keyof SignupFormValues) => (value: any) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  showConfirmPassword: boolean;
  onToggleConfirmPassword: () => void;
  onConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordBlur: () => void;
}

/**
 * Campos de "Dados do Usuário Administrador" (nome, email, senha, confirmar senha) do cadastro.
 * Extraído de SignupForm.tsx (F7).
 */
export const AdminAccountFields: React.FC<AdminAccountFieldsProps> = ({
  values,
  fieldErrors,
  handleChange,
  showPassword,
  onTogglePassword,
  showConfirmPassword,
  onToggleConfirmPassword,
  onConfirmPasswordChange,
  onPasswordBlur,
}) => {
  // Preenche senha e confirmação com uma senha aleatória que já atende a política do sistema
  // (validatePasswordFrontend) e revela os dois campos, pra dar pro usuário a chance de conferir
  // e copiar antes de continuar o cadastro.
  const handleGeneratePassword = () => {
    const generated = generateStrongPassword();
    handleChange('password')(generated);
    handleChange('confirmPassword')(generated);
    if (!showPassword) onTogglePassword();
    if (!showConfirmPassword) onToggleConfirmPassword();
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          Dados do Usuário Administrador
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Nome Completo"
          value={values.name}
          onChange={(e) => handleChange('name')(e.target.value)}
          autoFocus
          error={!!fieldErrors.name}
          helperText={fieldErrors.name || 'Digite seu nome completo'}
          inputProps={{ maxLength: 100 }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={values.email}
          onChange={(e) => handleChange('email')(e.target.value)}
          autoComplete="email"
          error={!!fieldErrors.email}
          helperText={fieldErrors.email || 'Digite um email válido'}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          value={values.password}
          onChange={(e) => handleChange('password')(e.target.value)}
          onBlur={onPasswordBlur}
          autoComplete="new-password"
          error={!!fieldErrors.password}
          helperText={
            fieldErrors.password ||
            'Mínimo 12 caracteres: maiúscula, minúscula, número e caractere especial'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Gerar senha forte automaticamente">
                  <IconButton onClick={handleGeneratePassword} edge="end" aria-label="gerar senha">
                    <AutorenewIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={onTogglePassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Confirmar Senha"
          type={showConfirmPassword ? 'text' : 'password'}
          value={values.confirmPassword}
          onChange={onConfirmPasswordChange}
          autoComplete="new-password"
          error={!!fieldErrors.confirmPassword}
          helperText={fieldErrors.confirmPassword || 'Digite a mesma senha novamente'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onToggleConfirmPassword} edge="end">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </>
  );
};

export default AdminAccountFields;
